import type {
  ContractUsageAvailability,
  ResolvedContractSegmentSummary,
} from '../types';

export type ContractUsageAvailabilityStatus =
  | 'unaffected'
  | 'partial'
  | 'unavailable';

export const describeContractUsageAvailability = (
  availability: ContractUsageAvailability,
): { label: string; status: ContractUsageAvailabilityStatus } => {
  const baseline = Number(availability.baseline_max_volume);
  const available = Number(availability.available_max_volume);
  const status =
    baseline > 0 && available <= 0
      ? 'unavailable'
      : available < baseline
      ? 'partial'
      : 'unaffected';

  return {
    label: `${available.toLocaleString('en-US')} Dth`,
    status,
  };
};

export const formatContractName = (
  contractName: string | null,
  contractKId: string | null,
) => {
  if (!contractName) return contractKId ?? 'N/A';
  if (!contractKId) return contractName;

  const combinedPrefix = `(${contractKId})`;
  if (!contractName.startsWith(combinedPrefix)) return contractName;

  return contractName.slice(combinedPrefix.length).trimStart() || contractKId;
};

type ContractIdentity = Pick<
  ResolvedContractSegmentSummary,
  'contract_id' | 'contract_k_id' | 'contract_name'
>;

export const getContractUsageLimiterLabels = (
  limitingContractIds: readonly number[] | undefined,
  contracts: readonly ContractIdentity[],
): string[] => {
  const contractsById = new Map(
    contracts
      .filter((contract) => contract.contract_id != null)
      .map((contract) => [contract.contract_id, contract]),
  );

  return [...new Set(limitingContractIds ?? [])].map((contractId) => {
    const contract = contractsById.get(contractId);
    const kId = contract?.contract_k_id;
    const name = contract?.contract_name
      ? formatContractName(contract.contract_name, kId ?? null)
      : null;

    if (kId && name && name !== kId) return `(${kId}) ${name}`;
    return kId ?? name ?? `Contract ${contractId}`;
  });
};
